import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit2, Trash2, CheckSquare } from 'lucide-react';
import { formatDateWord } from '@/lib/dateFormat';

const formatDate = (dateString) => {
  return formatDateWord(dateString);
};

// Get initials for avatar
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Get colorful avatar background based on name
const getAvatarColor = (name) => {
  if (!name) return 'bg-slate-500';
  
  const colors = [
    'bg-blue-600',
    'bg-purple-600',
    'bg-pink-600',
    'bg-green-600',
    'bg-yellow-600',
    'bg-red-600',
    'bg-indigo-600',
    'bg-teal-600',
    'bg-orange-600',
    'bg-cyan-600',
  ];
  
  // Simple hash function to consistently assign colors based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

const taskStatusConfig = {
  pending: { label: 'Pending', className: 'bg-slate-500/20 text-slate-600 border-slate-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-600 border-blue-500/30' },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-600 border-green-500/30' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500/20 text-red-600 border-red-500/30' },
};

const OrderTasksTab = ({ orderData, onOpenTaskDialog, onDeleteTask }) => {
  return (
    <Card className="bg-white border border-slate-200/50 shadow-sm">
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Tasks & Production Steps</CardTitle>
        <Button onClick={() => onOpenTaskDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {!orderData.tasks?.length ? (
          <div className="p-8 text-center text-slate-500">
            <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tasks assigned</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase">Title</TableHead>
                <TableHead className="text-xs uppercase">Assigned To</TableHead>
                <TableHead className="text-xs uppercase">Due Date</TableHead>
                <TableHead className="text-xs uppercase">Status</TableHead>
                <TableHead className="text-xs uppercase w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orderData.tasks.map((task) => (
                <TableRow key={task.task_id}>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {(() => {
                        if (!task.assigned_to) return <span className="text-muted-foreground">—</span>;
                        
                        // Handle both array and string formats
                        const users = Array.isArray(task.assigned_to)
                          ? task.assigned_to
                          : (typeof task.assigned_to === 'string'
                              ? task.assigned_to.split(',').map(u => u.trim()).filter(Boolean)
                              : [task.assigned_to]);
                        
                        if (users.length === 0) return <span className="text-muted-foreground">—</span>;
                        
                        return (
                          <>
                            {users.slice(0, 3).map((user, idx) => (
                              <Avatar key={idx} className="h-7 w-7">
                                <AvatarFallback className={`${getAvatarColor(user)} text-white text-[10px] font-medium`}>
                                  {getInitials(user)}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                            {users.length > 3 && (
                              <span className="text-xs text-muted-foreground ml-1">
                                +{users.length - 3}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(task.due_date)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={taskStatusConfig[task.status]?.className}>
                      {taskStatusConfig[task.status]?.label || task.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenTaskDialog(task)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDeleteTask(task.task_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderTasksTab;